/**
 * Asset requests: somebody asking for hardware they do not have yet.
 *
 * **A request is NOT an assignment, and that is the whole point of this file** - the
 * same lesson `repair-requests.ts` records about reports and repairs, applied to the
 * other end of the lifecycle.
 *
 * An `Assignment` is the register saying a named person physically has a laptop. An
 * `AssetRequest` is a sentence: "Priya starts on Monday and needs one." Nothing has
 * been decided, no asset has been picked, and the register must not move. If raising a
 * request could reserve or issue an asset, anyone could empty the available pool with a
 * form, and the register would stop matching what is on people's desks - the one thing
 * it exists to get right.
 *
 * So the flow is: somebody asks -> IT reviews -> IT either issues something against it
 * or declines it with a reason.
 *
 * ## Why FULFILLED is not a status anyone can choose
 *
 * `REPAIR_RAISED` on a repair request is not typed by IT either - it is what happens
 * when they create the actual Repair. FULFILLED works the same way: it is set only by
 * the assign flow, when a real handover is recorded against the request, and it carries
 * that handover's id.
 *
 * A dropdown offering "Fulfilled" would let IT close a request without issuing
 * anything, and the request would then claim someone has a laptop that the register has
 * never heard of. Two records disagreeing about who has what is worse than an open
 * request nobody has closed.
 *
 * ## What is deliberately missing
 *
 * **Quantity.** A manager onboarding three people raises three requests. One row with
 * `quantity: 3` cannot be satisfied by one handover, so it would need partial
 * fulfilment, a count of how many are outstanding, and a rule for what a half-met
 * request means at review. Three rows need none of that and each one closes against the
 * laptop that actually went out.
 */

export const ASSET_REQUEST_STATUSES = [
  'OPEN',
  'ACKNOWLEDGED',
  'FULFILLED',
  'DECLINED',
  'CANCELLED',
] as const;
export type AssetRequestStatus = (typeof ASSET_REQUEST_STATUSES)[number];

export const ASSET_REQUEST_STATUS_LABELS: Record<AssetRequestStatus, string> = {
  OPEN: 'Open',
  ACKNOWLEDGED: 'Seen by IT',
  FULFILLED: 'Issued',
  DECLINED: 'Declined',
  CANCELLED: 'Withdrawn',
};

export const ASSET_REQUEST_STATUS_BLURBS: Record<AssetRequestStatus, string> = {
  OPEN: 'Asked for, not yet looked at.',
  ACKNOWLEDGED: 'IT has seen it and is sourcing something.',
  FULFILLED: 'An asset has been issued against this request.',
  DECLINED: 'IT decided not to issue anything. The reason is on the request.',
  CANCELLED: 'Withdrawn by whoever raised it — no longer needed.',
};

/** Still needs someone to do something about it. Drives the IT queue and its badge. */
export function isAssetRequestOpen(status: AssetRequestStatus): boolean {
  return status === 'OPEN' || status === 'ACKNOWLEDGED';
}

/**
 * The outcomes IT may set by hand at review.
 *
 * OPEN is absent because it is where a request starts, and moving one back to "nobody
 * has looked at this" would erase the fact that somebody did. FULFILLED is absent
 * because it belongs to the assign flow - see the note at the top of this file.
 * CANCELLED is absent because withdrawing is the requester's call, not IT's: IT
 * declines, with a reason the requester can read.
 */
export const ASSET_REQUEST_REVIEW_OUTCOMES = ['ACKNOWLEDGED', 'DECLINED'] as const;
export type AssetRequestReviewOutcome = (typeof ASSET_REQUEST_REVIEW_OUTCOMES)[number];

export const MAX_ASSET_REQUEST_JUSTIFICATION = 2000;

/**
 * How overdue a request is against the date it was wanted by.
 *
 * `null` needed-by is 'NONE', not 'LATE'. Plenty of requests are "when you get a
 * chance" and flagging every one of them red is how the colour stops meaning anything -
 * the same reason a chair with no warranty is not shown as out of warranty.
 */
export type AssetRequestTiming = 'NONE' | 'FUTURE' | 'DUE_SOON' | 'LATE';

/** Inside this many days of the needed-by date reads as due soon. A week is roughly
 *  how long sourcing a laptop takes if it is not already on a shelf. */
export const ASSET_REQUEST_DUE_SOON_DAYS = 7;

export function assetRequestTiming(
  neededBy: string | null,
  today: string,
  windowDays: number = ASSET_REQUEST_DUE_SOON_DAYS,
): AssetRequestTiming {
  if (!neededBy) return 'NONE';
  if (neededBy < today) return 'LATE';
  const days = Math.round(
    (Date.parse(`${neededBy}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000,
  );
  return days <= windowDays ? 'DUE_SOON' : 'FUTURE';
}
